/**
 * Product-page postal / ZIP delivery check against Shopify Shipping & Delivery rates.
 */
class ZipValidator extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.form = this.querySelector('[data-zip-form]');
    this.input = this.querySelector('[data-zip-input]');
    this.button = this.querySelector('[data-zip-submit]');
    this.result = this.querySelector('[data-zip-result]');
    this.variantId = this.dataset.variantId;
    this.country = this.dataset.country || 'Canada';
    this.contactUrl = this.dataset.contactUrl || '/pages/contact';
    this.contactPhone = this.dataset.contactPhone || '';
    this.showPrice = this.dataset.showPrice !== 'false';
    this.currency = this.dataset.currency || 'CAD';
    this.checkingLabel = this.dataset.checkingLabel || 'Checking…';
    this.defaultButtonLabel = this.button ? this.button.textContent : 'Check';

    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.checkZip();
      });
    }

    document.addEventListener('variant:change', this.onVariantChange);
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const id = event?.data?.variant?.id;
        if (id) this.variantId = String(id);
      });
    }
  }

  disconnectedCallback() {
    if (this.unsubscribeVariant) this.unsubscribeVariant();
  }

  onVariantChange = (event) => {
    const id = event?.detail?.variant?.id;
    if (id) this.variantId = String(id);
  };

  normalizeZip(value) {
    return String(value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
  }

  formatCanadianZip(normalized) {
    if (normalized.length !== 6) return normalized;
    return `${normalized.slice(0, 3)} ${normalized.slice(3)}`;
  }

  isValidCanadianPostal(normalized) {
    return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\d[ABCEGHJ-NPRSTV-Z]\d$/i.test(normalized);
  }

  provinceFromPostal(normalized) {
    const map = {
      A: 'Newfoundland and Labrador',
      B: 'Nova Scotia',
      C: 'Prince Edward Island',
      E: 'New Brunswick',
      G: 'Quebec',
      H: 'Quebec',
      J: 'Quebec',
      K: 'Ontario',
      L: 'Ontario',
      M: 'Ontario',
      N: 'Ontario',
      P: 'Ontario',
      R: 'Manitoba',
      S: 'Saskatchewan',
      T: 'Alberta',
      V: 'British Columbia',
      X: 'Northwest Territories',
      Y: 'Yukon',
    };
    return map[normalized.charAt(0)] || '';
  }

  setLoading(isLoading) {
    if (!this.button) return;
    this.button.disabled = isLoading;
    this.button.textContent = isLoading ? this.checkingLabel : this.defaultButtonLabel;
  }

  showResult(type, html) {
    if (!this.result) return;
    this.result.hidden = false;
    this.result.className = `zip-validator__result zip-validator__result--${type}`;
    this.result.innerHTML = html;
    this.result.setAttribute('role', 'status');
  }

  formatPrice(amount) {
    const value = Number(amount);
    if (Number.isNaN(value)) return amount;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency: this.currency,
      }).format(value);
    } catch (e) {
      return `$${value.toFixed(2)}`;
    }
  }

  buildRatesHtml(rates) {
    if (!this.showPrice || !rates.length) return '';
    const items = rates
      .map((rate) => {
        const name = rate.presentment_name || rate.name || rate.title || 'Delivery';
        const price = this.formatPrice(rate.price);
        return `<li><span>${name}</span><strong>${price}</strong></li>`;
      })
      .join('');
    return `<ul class="zip-validator__rates">${items}</ul>`;
  }

  contactHtml() {
    const phone = this.contactPhone
      ? `<p>Or call us at <a class="zip-validator__contact" href="tel:${this.contactPhone.replace(/[^\d+]/g, '')}">${this.contactPhone}</a>.</p>`
      : '';
    return `
      <p>We don’t currently deliver to this postal code.</p>
      <p>Please <a class="zip-validator__contact" href="${this.contactUrl}">contact us</a> for a custom shipping quote.</p>
      ${phone}
    `;
  }

  async ensureCartHasItem() {
    const cart = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
      credentials: 'same-origin',
    }).then((r) => r.json());

    if (cart.item_count > 0 && cart.requires_shipping) {
      return { restored: false, key: null };
    }

    if (!this.variantId) {
      throw new Error('No product variant available to check shipping.');
    }

    const added = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(this.variantId),
            quantity: 1,
            properties: { _zip_check: '1' },
          },
        ],
      }),
    }).then(async (r) => {
      const data = await r.json();
      if (!r.ok) throw new Error(data.description || data.message || 'Could not prepare shipping check.');
      return data;
    });

    const item = added.items?.[0] || added;
    return { restored: true, key: item.key };
  }

  async restoreCart(tempKey) {
    if (!tempKey) return;
    await fetch(window.routes?.cart_change_url || '/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: tempKey, quantity: 0 }),
    }).catch(() => {});
  }

  buildAddressParams(zip, province) {
    const params = new URLSearchParams();
    params.set('shipping_address[zip]', zip);
    params.set('shipping_address[country]', this.country);
    if (province) params.set('shipping_address[province]', province);
    return params;
  }

  async fetchShippingRates(params) {
    const syncUrl = `/cart/shipping_rates.json?${params.toString()}`;
    const syncRes = await fetch(syncUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (syncRes.ok) {
      const data = await syncRes.json();
      if (Array.isArray(data.shipping_rates)) return data.shipping_rates;
    }

    // Carrier-calculated / async fallback
    await fetch(`/cart/prepare_shipping_rates.json?${params.toString()}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    for (let attempt = 0; attempt < 8; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 350));
      const asyncRes = await fetch(`/cart/async_shipping_rates.json?${params.toString()}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!asyncRes.ok) continue;
      const asyncData = await asyncRes.json();
      if (asyncData && Array.isArray(asyncData.shipping_rates)) {
        return asyncData.shipping_rates;
      }
    }

    return [];
  }

  async checkZip() {
    const normalized = this.normalizeZip(this.input?.value);
    const displayZip = this.formatCanadianZip(normalized);

    if (!normalized) {
      this.showResult('info', '<p>Enter your postal code to check delivery.</p>');
      return;
    }

    if (!this.isValidCanadianPostal(normalized)) {
      this.showResult('info', '<p>Please enter a valid Canadian postal code (e.g. B1L 1B8).</p>');
      return;
    }

    this.setLoading(true);
    this.showResult('info', '<p>Checking delivery for your area…</p>');

    let tempKey = null;
    try {
      const ensure = await this.ensureCartHasItem();
      tempKey = ensure.key;

      const province = this.provinceFromPostal(normalized);
      const params = this.buildAddressParams(displayZip, province);
      const rates = await this.fetchShippingRates(params);

      if (rates.length > 0) {
        const successCopy =
          this.dataset.successMessage || 'Yes — we deliver to your area.';
        this.showResult(
          'success',
          `<p>${successCopy}</p><p>Postal code: <strong>${displayZip}</strong></p>${this.buildRatesHtml(rates)}`
        );
      } else {
        this.showResult('error', this.contactHtml());
      }
    } catch (error) {
      this.showResult(
        'error',
        `<p>We couldn’t verify delivery right now.</p>${this.contactHtml()}`
      );
    } finally {
      await this.restoreCart(tempKey);
      this.setLoading(false);
    }
  }
}

if (!customElements.get('zip-validator')) {
  customElements.define('zip-validator', ZipValidator);
}
